import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, HostListener, ElementRef, ViewChild } from '@angular/core';
import { Router, NavigationStart } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AuthenticationService } from 'src/app/modules/shared/services/authentication.service';
import { ExternalAuthService } from 'src/app/modules/external/services/external-auth.service';
import { Configuration } from 'src/app/app.constants';
import { DocumentViewModel } from '../../../viewModels/documents/documentViewModel';
import { TranslateService } from '@ngx-translate/core';
import { FilterViewModel } from '../../../models/filter/filterViewModel';
import { AllowedFiltersService } from '../../../services/allowed-filters.service';
import { FilterTypes } from '../../../enums/filterTypes';
import moment from 'moment';
import { AttachmentsEventsEmitter } from '../../../events/attachments.events';
import { UploadTypes } from '../../../enums/uploadTypes';
import { UrlService } from '../../../services/url.service';
import { ObjectTypes } from '../../../enums/objectTypes';
import { ChecklistOptionSet1, ChecklistOptionSet2, ChecklistResponseTypes } from '../../../enums/checklists/checklistEnums';
import { T } from 'src/assets/i18n/translation-keys';
import { DocumentService } from '../../../services/document.service';

@Component({
    selector: 'app-ng-image-gallery-preview',
    templateUrl: './ngImageGalleryPreview.component.html',
    styleUrls: ['./ngImageGalleryPreview.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class NgImageGalleryPreviewComponent implements OnInit, OnDestroy {
  @ViewChild('thumbnailStrip', { static: false }) thumbnailStrip: ElementRef;
  public visible: boolean = false;
  public imageDocuments: DocumentViewModel[] = [];
  public currentImage: DocumentViewModel | null;
  public currentIndex: number = 0;
  public createdDate: string;
  public user: FilterViewModel;
  private users: FilterViewModel[] = [];
  public contextualInfo: any = null;
  public objectTypes = ObjectTypes;
  public readonly T = T;
  private subscriptions: Subscription = new Subscription();
  private isDownloading: boolean = false;
  public isImageLoading: boolean = false;


  constructor(
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly authenticationService: AuthenticationService,
    private readonly externalAuthService: ExternalAuthService,
    private readonly configuration: Configuration,
    protected readonly translateService: TranslateService,
    protected readonly allowedFiltersService: AllowedFiltersService,
    protected readonly attachmentsEventsEmitter: AttachmentsEventsEmitter,
    private readonly urlService: UrlService,
    private readonly documentService: DocumentService,
    private readonly router: Router
  ) {}

  public ngOnInit() {
    this.users = this.allowedFiltersService.getCachedAllowedFiltersByType(FilterTypes.Employee);
    this.setupAttachmentDeletedSubscription();
    this.setupNavigationSubscription();
  }

  private setupAttachmentDeletedSubscription(): void {
    this.subscriptions.add(
      this.attachmentsEventsEmitter.attachmentDeletedSuccessfully$.subscribe((deletedDocument) => {
        if (this.currentImage && deletedDocument.id === this.currentImage.id) {
          this.removeImageFromGallery();
        }
      })
    );
  }

  private setupNavigationSubscription(): void {
    this.subscriptions.add(
      this.router.events
        .pipe(filter((event) => event instanceof NavigationStart))
        .subscribe(() => {
          if (this.visible) {
            this.hide();
          }
        })
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public show(imageDocuments: DocumentViewModel[], selectedImage: DocumentViewModel, contextualInfo?: any): void {
    this.imageDocuments = [...imageDocuments];
    this.currentIndex = this.imageDocuments.findIndex((img) => img.id === selectedImage.id);
    if (this.currentIndex === -1) {
      this.currentIndex = 0;
    }
    this.currentImage = this.imageDocuments[this.currentIndex];
    this.contextualInfo = contextualInfo;
    this.updateCurrentImageInfo();
    this.visible = true;
    this.isImageLoading = true;
    this.changeDetectorRef.markForCheck();
  }

  public hide(): void {
    this.visible = false;
    this.imageDocuments = [];
    this.currentImage = null;
    this.user = null;
    this.createdDate = null;
    this.contextualInfo = null;
    this.currentIndex = 0;
    this.isImageLoading = false;
    this.changeDetectorRef.markForCheck();
  }

  public nextImage(): void {
    if (this.currentIndex < this.imageDocuments.length - 1) {
      this.isImageLoading = true;
      this.currentIndex++;
      this.currentImage = this.imageDocuments[this.currentIndex];
      this.updateCurrentImageInfo();
      this.scrollToActiveThumbnail();
      this.changeDetectorRef.markForCheck();
    }
  }

  public previousImage(): void {
    if (this.currentIndex > 0) {
      this.isImageLoading = true;
      this.currentIndex--;
      this.currentImage = this.imageDocuments[this.currentIndex];
      this.updateCurrentImageInfo();
      this.scrollToActiveThumbnail();
      this.changeDetectorRef.markForCheck();
    }
  }

  public goToImage(index: number): void {
    if (index >= 0 && index < this.imageDocuments.length) {
      this.isImageLoading = true;
      this.currentIndex = index;
      this.currentImage = this.imageDocuments[index];
      this.updateCurrentImageInfo();
      this.scrollToActiveThumbnail();
      this.changeDetectorRef.markForCheck();
    }
  }

  private updateCurrentImageInfo(): void {
    if (this.currentImage) {
      this.createdDate = moment(this.currentImage.created).format('DD MMM YYYY HH:mm');
      this.user = this.users.find((x) => x.filterValue == this.currentImage.createdById);

      const currentImageContextualInfo = (this.currentImage as any).contextualInfo;
      if (currentImageContextualInfo) {
        this.contextualInfo = currentImageContextualInfo;
      }
    }
  }

  public currentImageUrl(): string | null {
    return this.getImageUrl(this.currentImage);
  }

  public onImageLoad(): void {
    this.isImageLoading = false;
    this.changeDetectorRef.markForCheck();
  }

  public onImageError(): void {
    this.isImageLoading = false;
    this.changeDetectorRef.markForCheck();
  }

  private getAccountId(): number {
    const externalSession = this.externalAuthService.getCurrentSession();
    if (externalSession && this.externalAuthService.isAuthenticated()) {
      return externalSession.accountId || 0;
    }
    return this.authenticationService.getCurrentAccount()?.id || 0;
  }

  public get isExternalUser(): boolean {
    return this.externalAuthService.isAuthenticated();
  }

  public getImageUrl(document: DocumentViewModel): string {
    if (!document) return '';

    let resourceUrl = document.filename;
    if (resourceUrl) {
      resourceUrl = resourceUrl.toLowerCase().startsWith(this.configuration.ResourceFolderName.toLowerCase())
        ? resourceUrl
        : `/${this.configuration.ResourceFolderName}/${this.getAccountId()}/${resourceUrl}`;
    }

    const fullUrl = document.uploadType === UploadTypes.File
      ? this.buildResourceUrl(resourceUrl)
      : document.filename;

    const indexOfResource = fullUrl.toLowerCase().indexOf(`/${this.configuration.ResourceFolderName.toLowerCase()}/`);
    const lastIndexOfDash = fullUrl.lastIndexOf('/');

    if (indexOfResource !== -1 || lastIndexOfDash === -1) {
      return fullUrl;
    }

    if (lastIndexOfDash) {
      return (
        fullUrl.slice(0, lastIndexOfDash) +
        `/${this.configuration.ResourceFolderName}/${this.getAccountId()}` +
        fullUrl.slice(lastIndexOfDash)
      );
    }

    return fullUrl;
  }

  private buildResourceUrl(resourceUrl: string): string {
    return this.urlService.buildResourceUrl(resourceUrl);
  }

  public downloadAttachment(): void {
    if (this.isDownloading || !this.currentImage) {
      return;
    }

    this.isDownloading = true;
    this.documentService.downloadDocument(this.currentImage.id);

    setTimeout(() => {
      this.isDownloading = false;
    }, 1000);
  }

  public deleteAttachment(): void {
    this.attachmentsEventsEmitter.broadcastAttachmentDeleted(this.currentImage);
  }

  private removeImageFromGallery(): void {
    const deletedIndex = this.currentIndex;
    this.imageDocuments = this.imageDocuments.filter((img) => img.id !== this.currentImage.id);

    if (this.imageDocuments.length === 0) {
      this.hide();
    } else {
      this.navigateAfterDeletion(deletedIndex);
    }
  }

  private navigateAfterDeletion(deletedIndex: number): void {
    if (deletedIndex >= this.imageDocuments.length) {
      this.currentIndex = this.imageDocuments.length - 1;
    } else {
      this.currentIndex = deletedIndex;
    }

    this.currentImage = this.imageDocuments[this.currentIndex];
    this.updateCurrentImageInfo();
    this.changeDetectorRef.markForCheck();
  }

  @HostListener('document:keydown', ['$event'])
  public onKeyDown(event: KeyboardEvent): void {
    if (!this.visible) return;

    switch (event.key) {
      case 'Escape':
        this.hide();
        break;
      case 'ArrowLeft':
        this.previousImage();
        break;
      case 'ArrowRight':
        this.nextImage();
        break;
    }
  }

  // this could be extended to other object types in the future
  public getContextualInfoForDisplay(): any {
    if (!this.contextualInfo) {
      return null;
    }

    const objectType = this.contextualInfo.objectType || this.currentImage?.globalObjectType;

    switch (objectType) {
      case ObjectTypes.Checklist_Item:
        return this.getChecklistItemContextualInfo();
      default:
        return null;
    }
  }

  private getChecklistItemContextualInfo(): any {
    if (!this.contextualInfo) {
      return null;
    }

    return {
      title: this.contextualInfo.title,
      responseType: this.contextualInfo.responseType,
      response: this.contextualInfo.response,
      textResponse: this.contextualInfo.textResponse
    };
  }

  public getCheckResponseDisplay(): string {
    if (!this.contextualInfo) {
      return '';
    }

    const responseType = this.contextualInfo.responseType;
    const response = this.contextualInfo.response;

    switch (responseType) {
      case ChecklistResponseTypes.Mark_as_Complete:
        return response === 'true' ? this.translateService.instant(T.common.completed.one) : this.translateService.instant(T.common.not_completed);

      case ChecklistResponseTypes.Text_Response:
        return response || this.translateService.instant(T.common.no_responses);

      case ChecklistResponseTypes.Options_Set_1:
        const responseValue1 = +response;
        const optionSet1Item = ChecklistOptionSet1.items().find((x) => x.key === responseValue1);
        return optionSet1Item?.value || this.translateService.instant(T.common.no_responses);

      case ChecklistResponseTypes.Options_Set_2:
        const responseValue2 = +response;
        const optionSet2Item = ChecklistOptionSet2.items().find((x) => x.key === responseValue2);
        return optionSet2Item?.value || this.translateService.instant(T.common.no_responses);

      case ChecklistResponseTypes.Numeric_Response:
        return response || this.translateService.instant(T.common.no_responses);

      default:
        return response || this.translateService.instant(T.common.no_responses);
    }
  }

  private scrollToActiveThumbnail(): void {
    if (!this.thumbnailStrip) return;

    setTimeout(() => {
      const thumbnailContainer = this.thumbnailStrip.nativeElement;
      const activeThumbnail = thumbnailContainer.querySelector('.thumbnail.active') as HTMLElement;

      if (activeThumbnail && thumbnailContainer) {
        const containerWidth = thumbnailContainer.clientWidth;
        const scrollWidth = thumbnailContainer.scrollWidth;

        if (scrollWidth > containerWidth) {
          const thumbnailLeft = activeThumbnail.offsetLeft;
          const thumbnailWidth = activeThumbnail.clientWidth;

          const targetScroll = thumbnailLeft - (containerWidth / 2) + (thumbnailWidth / 2);

          thumbnailContainer.scrollTo({
            left: Math.max(0, Math.min(targetScroll, scrollWidth - containerWidth)),
            behavior: 'smooth'
          });
        }
      }
    }, 50);
  }

  public scrollThumbnailsLeft(): void {
    if (!this.thumbnailStrip) return;

    const container = this.thumbnailStrip.nativeElement;
    const scrollAmount = container.clientWidth * 0.8;

    container.scrollTo({
      left: container.scrollLeft - scrollAmount,
      behavior: 'smooth'
    });
  }

  public scrollThumbnailsRight(): void {
    if (!this.thumbnailStrip) return;

    const container = this.thumbnailStrip.nativeElement;
    const scrollAmount = container.clientWidth * 0.8;

    container.scrollTo({
      left: container.scrollLeft + scrollAmount,
      behavior: 'smooth'
    });
  }

  public get canScrollThumbnailsLeft(): boolean {
    if (!this.thumbnailStrip) return false;
    return this.thumbnailStrip.nativeElement.scrollLeft > 0;
  }

  public get canScrollThumbnailsRight(): boolean {
    if (!this.thumbnailStrip) return false;
    const container = this.thumbnailStrip.nativeElement;
    return container.scrollLeft < (container.scrollWidth - container.clientWidth);
  }
}

